import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { ResourceManagmentApi } from 'api/resource-management';
import { ResourceManagementConfig } from 'api/resource-management/types';
import { GENERAL_API_STALE } from 'config/query-config';

export type ResourceManagementConfigQueryKey = [
  'resource-management',
  'config',
];

export const getResourceManagementConfigQueryKey =
  (): ResourceManagementConfigQueryKey => ['resource-management', 'config'];

export const useResourceManagementConfigQuery = (
  queryOptions: UseQueryOptions<
    ResourceManagementConfig,
    unknown,
    ResourceManagementConfig,
    ResourceManagementConfigQueryKey
  > = {},
): UseQueryResult<ResourceManagementConfig> =>
  useQuery(
    getResourceManagementConfigQueryKey(),
    () => ResourceManagmentApi.getResourceManagementConfig(),
    {
      staleTime: GENERAL_API_STALE,
      ...queryOptions,
    },
  );

export const useResourceManagementConfigMutation = (
  options: UseMutationOptions<
    ResourceManagementConfig,
    unknown,
    ResourceManagementConfig,
    ResourceManagementConfig
  > = {},
): UseMutationResult<
  ResourceManagementConfig,
  unknown,
  ResourceManagementConfig,
  ResourceManagementConfig
> => {
  const queryClient = useQueryClient();

  return useMutation(
    (config) => ResourceManagmentApi.updateResourceManagementConfig(config),
    {
      onMutate: (updatedConfig) => {
        queryClient.setQueryData<ResourceManagementConfig>(
          getResourceManagementConfigQueryKey(),
          (previousConfig) => ({ ...previousConfig, ...updatedConfig }),
        );

        return queryClient.getQueryData<ResourceManagementConfig>(
          getResourceManagementConfigQueryKey(),
        );
      },
      onError: (error, data, fallback) => {
        queryClient.setQueryData<ResourceManagementConfig>(
          getResourceManagementConfigQueryKey(),
          () => fallback as ResourceManagementConfig,
        );
      },
      ...options,
    },
  );
};

export const useResourceManagementPlanningLevelMutation = (): UseMutationResult<
  ResourceManagementConfig,
  unknown,
  string,
  ResourceManagementConfig
> => {
  const queryClient = useQueryClient();

  return useMutation(
    (config) =>
      ResourceManagmentApi.updateResourceManagementPlanningLevel(config),
    {
      onMutate: (updatedConfig) => {
        queryClient.setQueryData<ResourceManagementConfig>(
          getResourceManagementConfigQueryKey(),
          (previousConfig) =>
            ({
              ...previousConfig,
              planningLevelConfig: updatedConfig,
            } as ResourceManagementConfig),
        );

        return queryClient.getQueryData<ResourceManagementConfig>(
          getResourceManagementConfigQueryKey(),
        );
      },
      onError: (error, data, fallback) => {
        queryClient.setQueryData<ResourceManagementConfig>(
          getResourceManagementConfigQueryKey(),
          () => fallback as ResourceManagementConfig,
        );
      },
    },
  );
};
